import { ChangeDetectorRef, Component, ComponentFactoryResolver, ComponentRef, forwardRef, HostBinding, Inject, Input, OnDestroy, OnInit, Optional, TemplateRef, Type, ViewChild } from "@angular/core";
import { ResponsiveService } from "@app/services/responsive.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ComponentAnchorDirective } from "../../_directives/componentAnchor.directive";
import { IVTableCell } from "../../_models/IVTableCell";
import { VTableComponent } from "../v-table.component";
import './v-table-cell.component.scss';

@UntilDestroy()
@Component({
    selector: 'v-table-cell',
    template: require('./v-table-cell.component.html')
})
export class VTableCell implements OnInit {
    @Input() cell: IVTableCell;

    @ViewChild(ComponentAnchorDirective) componentHost: ComponentAnchorDirective;
    @HostBinding("style.display") display: string;
    @HostBinding("class") classes: string;
    @HostBinding("class.sorted") @Input() sorted: boolean;

    //--

    templateContext;
    componentRef: ComponentRef<any>;

    constructor(
        @Optional() @Inject(forwardRef(() => VTableComponent)) private table: VTableComponent,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly ComponentFactoryResolver: ComponentFactoryResolver,
        private readonly responsiveService: ResponsiveService) {

    }

    ngOnInit(): void {
        this.initialise();
    }

    ngOnChanges(): void {
        if (this.cell.component && this.componentHost) {
            this.componentHost.viewContainerRef.clear();
            this.loadComponent();
        }

        this.setClassesAndStyles();
    }

    ngAfterViewInit(): void {
        if (this.cell.component && this.componentHost) {
            this.loadComponent();
        }

        if (this.cell.template) {
            this.templateContext = {
                $implicit: this.cell.data,
            }

            this.changeDetectorRef.detectChanges();
        }

        this.setClassesAndStyles();
    }

    ngOnDestroy(): void {
        this.componentRef?.destroy();
        this.componentRef = null;
        this.changeDetectorRef.detach();
    }

    setClassesAndStyles(): void {
        this.classes = this.cell.classes ?? "";

        if (!this.cell.header) {
            return;
        }
        
        if (this.cell.header.width) {
            this.classes += ` width-${this.cell.header.width}`;
        }

        if (this.cell.header.minWidth) {
            this.classes += ` min-width-${this.cell.header.minWidth}`;
        }
    }

    //--

    private loadComponent(): void {
        const componentFactory = this.ComponentFactoryResolver.resolveComponentFactory(this.cell.component);
        this.componentHost.viewContainerRef.clear();
        this.componentRef = this.componentHost.viewContainerRef.createComponent(componentFactory);

        if (this.cell.componentInputs) {
            Object.keys(this.cell.componentInputs).forEach(key => {
                this.componentRef.instance[key] = this.cell.componentInputs[key];
            })
        }
        
        if (this.cell.componentOutputs) {
            Object.keys(this.cell.componentOutputs).forEach(key => {
                this.componentRef.instance[key].subscribe(this.cell.componentOutputs[key]);
            })
        }

        this.componentRef.changeDetectorRef.detectChanges();
    }

    private initialise(): void {
        this.setDisplay();
        this.setClassesAndStyles();

        this.responsiveService.currentWindowWidth.pipe(untilDestroyed(this)).subscribe(() => {
            this.setDisplay();
        })
    }

    private setDisplay(): void {
        if (!this.cell.header?.minTableWidth) {
            return;
        }

        this.display = this.responsiveService.shouldDisplayUsingMinWidth(this.cell.header.minTableWidth, this.table.getWidth()) ? null : "none";
    }

}
